import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faCalendarAlt, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiClient from "../../../../../services/apiClient";
import apiServices from "../../../../../services/apiService";

const ManagePreApprovals = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    
    // Usually Unit Admins are tied to a specific site and unit
    const assignedSiteId = user?.siteId || 2;
    const unitId = user?.branchorunitID || user?.unitId;

    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);

    const [staffList, setStaffList] = useState([]);
    
    // Fallback if UnitAdmin can select unit
    const [units, setUnits] = useState([]);

    const [formData, setFormData] = useState({
        staffId: null,
        siteId: assignedSiteId,
        unitId: unitId || null,
        validFrom: "",
        validTo: "",
        action: "Add"
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchInitialData = async () => {
            setFetchingData(true);
            try {
                // Fetch all active staff across vendors for this site (or globally if allowed)
                // This might need a specific endpoint for Unit Admins to see all vendor staff
                const staffRes = await apiClient.get('/staff-management/staff/GetStaff', {
                    params: { siteId: assignedSiteId, status: 'Active' }
                });
                
                if (staffRes.data?.isSuccess && staffRes.data?.data) {
                    const activeStaff = staffRes.data.data.filter(s => s.status === 'Active' || s.employmentStatus === 'Active');
                    setStaffList(activeStaff.map(s => ({
                        label: `${s.firstName || ''} ${s.lastName || ''} (${s.vendorName || 'No Vendor'})`,
                        value: s.staffId || s.id
                    })));
                }

                // If unitId is not assigned to the user, fetch units for the site
                if (!unitId) {
                    const unitRes = await apiServices({
                        method: 'GET',
                        url: `/UnitMaster/GetAllUnitMaster?siteId=${assignedSiteId}&pageNumber=1&pageSize=1000`
                    });
                    if (unitRes.isSuccess || unitRes.success || unitRes.IsSuccess) {
                        const allUnits = unitRes.data?.data || unitRes.data || [];
                        setUnits(allUnits.map(u => ({
                            label: u.UnitName || u.unitName,
                            value: u.Id || u.UnitId || u.unitId
                        })));
                    }
                }
            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setFetchingData(false);
            }
        };

        fetchInitialData();
    }, [assignedSiteId, unitId]);

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.staffId) newErrors.staffId = "Staff selection is required.";
        if (!formData.siteId) newErrors.siteId = "Site is required.";
        if (!formData.unitId) newErrors.unitId = "Unit is required.";
        if (!formData.validFrom) newErrors.validFrom = "Start Date is required.";
        if (!formData.validTo) newErrors.validTo = "End Date is required.";
        else if (new Date(formData.validFrom) > new Date(formData.validTo)) newErrors.validTo = "End date must be after start date.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }

        setLoading(true);

        const payload = {
            staffId: [formData.staffId],
            siteId: formData.siteId,
            unitId: formData.unitId,
            validFrom: new Date(formData.validFrom).toISOString(),
            validTo: new Date(formData.validTo).toISOString(),
            action: formData.action, // Add or Remove
            createdBy: user?.name || user?.userName || "UnitAdmin",
            correlationId: crypto.randomUUID()
        };

        try {
            const response = await apiClient.post('/staff-management/staff/ManagePreApproval', payload);
            
            if (response.data?.isSuccess) {
                toast.success(response.data?.message || `Pre-Approval ${formData.action === 'Add' ? 'added' : 'removed'} successfully.`);
                navigate("/zentres/workforce-management/approvals/dashboard");
            } else {
                toast.error(response.data?.message || "Failed to process pre-approval.");
            }
        } catch (error) {
            console.error("Pre-Approval error:", error);
            toast.error(error.response?.data?.message || "An error occurred while managing pre-approval.");
        } finally {
            setLoading(false);
        }
    };

    if (fetchingData) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 min-h-0 pb-12">
            <Breadcrumbs items={["Workforce Management", "Approvals", "Manage Pre-Approvals"]} />

            <div className="mb-6 mt-4">
                <PageHeadingSection
                    Navbar={true}
                    mainHeading="Manage Pre-Approvals"
                    subHeading="Pre-approve specific vendor staff so they bypass the manual access request process for a given timeframe."
                />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col max-w-3xl">
                <form onSubmit={handleSubmit} className="p-6 md:p-8 flex flex-col">
                    <div className="flex flex-col gap-8">
                        
                        {/* Action Selection */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-2">
                                Pre-Approval Action <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="actionType" 
                                        value="Add"
                                        checked={formData.action === "Add"}
                                        onChange={(e) => setFormData(prev => ({...prev, action: e.target.value}))}
                                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Grant Pre-Approval</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input 
                                        type="radio" 
                                        name="actionType" 
                                        value="Remove"
                                        checked={formData.action === "Remove"}
                                        onChange={(e) => setFormData(prev => ({...prev, action: e.target.value}))}
                                        className="w-4 h-4 text-red-600 focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Revoke Pre-Approval</span>
                                </label>
                            </div>
                        </div>

                        {/* Staff Selection */}
                        <div className="grid grid-cols-1 gap-5">
                            <div>
                                <CustomSelect
                                    label={<>Select Staff Member <span className="text-red-500">*</span></>}
                                    options={staffList}
                                    value={staffList.find(s => s.value === formData.staffId) || null}
                                    onChange={(opt) => handleSelectChange('staffId', opt)}
                                    error={errors.staffId}
                                />
                                {errors.staffId && <p className="text-red-500 text-[10px] mt-1">{errors.staffId}</p>}
                                <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                                    <FontAwesomeIcon icon={faShieldAlt} className="text-orange-400" />
                                    This staff member will automatically be set to 'Active' upon vendor duty allotment.
                                </p>
                            </div>
                        </div>

                        {/* Location Selection (if unitId not tied to user) */}
                        {!unitId && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <CustomSelect
                                        label={<>Unit <span className="text-red-500">*</span></>}
                                        options={units}
                                        value={units.find(u => u.value === formData.unitId) || null}
                                        onChange={(opt) => handleSelectChange('unitId', opt)}
                                        error={errors.unitId}
                                    />
                                    {errors.unitId && <p className="text-red-500 text-[10px] mt-1">{errors.unitId}</p>}
                                </div>
                            </div>
                        )}

                        {/* Timeframe */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Valid From <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        type="datetime-local" 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.validFrom ? 'border-red-500' : 'border-gray-200'}`}
                                        value={formData.validFrom}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, validFrom: e.target.value }));
                                            if (errors.validFrom) setErrors(prev => ({ ...prev, validFrom: null }));
                                        }}
                                    />
                                </div>
                                {errors.validFrom && <p className="text-red-500 text-[10px] mt-1">{errors.validFrom}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Valid To <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        type="datetime-local" 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${errors.validTo ? 'border-red-500' : 'border-gray-200'}`}
                                        value={formData.validTo}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, validTo: e.target.value }));
                                            if (errors.validTo) setErrors(prev => ({ ...prev, validTo: null }));
                                        }}
                                    />
                                </div>
                                {errors.validTo && <p className="text-red-500 text-[10px] mt-1">{errors.validTo}</p>}
                            </div>
                        </div>

                    </div>

                    <div className="flex justify-between mt-10 border-t pt-4 shrink-0">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-3 py-2 border border-gray-200 rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 text-white ${formData.action === 'Add' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                            >
                                {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (formData.action === 'Add' ? "Grant Pre-Approval" : "Revoke Pre-Approval")}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManagePreApprovals;
